import { createSelector } from 'reselect';

import type { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';

import { MODAL_PRIVACY_PREFERENCES } from 'Store/modals/constants';
import { selectCurrentModal } from 'Store/modals/selectors';

import type {
  PrivacyCategoryData,
  PrivacyConsentData,
  PrivacyConsentMetadata,
  PrivacyState,
} from './types';

/**
 * Selects privacy slice of the state
 */
export const selectPrivacy = createKeySelector('privacy');

export const getShowPrivacyPrompt = createSelector(
  [selectPrivacy, selectCurrentModal],
  (privacy, currentModal): boolean =>
    !(currentModal?.type === MODAL_PRIVACY_PREFERENCES) && privacy.showPrompt
);

export const getPrivacyUserConsents = createSelector(
  selectPrivacy,
  (privacy): Record<string, boolean> => privacy.consents
);

export const getPrivacyCategoryData = createSelector(
  selectPrivacy,
  (privacy): Record<string, PrivacyCategoryData> => privacy.categoryData
);

export const getPrivacyConsentData = createSelector(
  selectPrivacy,
  (privacy): Record<string, PrivacyConsentData> => privacy.consentData
);

export const getPrivacyCategoryOrder = createSelector(
  selectPrivacy,
  (privacy): string[] => privacy.categoryOrder
);

export const getPrivacyDefaultConsents = createSelector(
  [getPrivacyCategoryData, getPrivacyConsentData],
  (categoryData, consentData): Record<string, boolean> => {
    const defaultConsents: Record<string, boolean> = {};

    (Object.entries(categoryData) as any[]).forEach(
      ([, category]: [string, PrivacyCategoryData]) => {
        defaultConsents[`:${category.code}`] = category.defaultValue;
      }
    );

    // Consent preferences that determine whether or not a tracker is enabled
    (Object.entries(consentData) as any[]).forEach(([, consent]: [string, PrivacyConsentData]) => {
      defaultConsents[consent.code] =
        consent.enabled == null ? consent.defaultValue : consent.enabled;
    });

    return defaultConsents;
  }
);

export const getPrivacyConsents = createSelector(
  [getPrivacyDefaultConsents, getPrivacyUserConsents],
  (defaultConsents, userConsents): Record<string, boolean> => ({
    ...defaultConsents,
    ...userConsents,
  })
);

// Mapping of categories to the consents they contain
export const getPrivacyConsentsByCategory = createSelector(
  getPrivacyCategoryData,
  (categoryData): Record<string, PrivacyCategoryData['consents']> => {
    const consentsByCategory: Record<string, PrivacyCategoryData['consents']> = {};
    Object.entries(categoryData).forEach(([key, category]) => {
      consentsByCategory[key] = category.consents;
    });
    return consentsByCategory;
  }
);

export const getConsentLevel2Metadata = createSelector(
  [getPrivacyCategoryOrder, getPrivacyCategoryData],
  (categoryOrder, categoryData): PrivacyCategoryData[] =>
    categoryOrder.map((categoryCode) => categoryData[categoryCode])
);

export const getConsentLevel3Metadata = createSelector(
  [
    (state: GlobalStateType, categoryCode: string) =>
      getPrivacyConsentsByCategory(state)[categoryCode],
    getPrivacyConsentData,
  ],
  (consentOrder, consentData): PrivacyConsentData[] => consentOrder.map((code) => consentData[code])
);

export const getConsentLevel4Metadata = createSelector(
  [(_state: GlobalStateType, code: string) => code, getPrivacyConsentData],
  (code, consentData): PrivacyConsentMetadata[][] => consentData[code].metadata
);

export const getRequiresInput = createSelector(
  [getPrivacyCategoryData, getPrivacyConsentData],
  (categoryData, consentData): boolean =>
    Object.entries(categoryData).reduce(
      (requiresInput, [, category]: [string, PrivacyCategoryData]) =>
        requiresInput || category.requiresInput,
      false
    ) ||
    Object.entries(consentData).reduce(
      (requiresInput, [, consent]: [string, PrivacyConsentData]) =>
        requiresInput || consent.requiresInput,
      false
    )
);

export const getNeedsPrivacyPrompt = createSelector(
  [selectPrivacy, getRequiresInput],
  (privacy: PrivacyState, requiresInput: boolean): boolean => privacy.showPrompt || requiresInput
);

export const getHasDeferredUpdate = createSelector(
  selectPrivacy,
  (privacy): boolean => privacy.hasDeferredUpdate || false
);
